import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, Select, Input, Modal, Form, message, Descriptions } from 'antd';
import { ReloadOutlined, CheckOutlined, CloseOutlined, EyeOutlined } from '@ant-design/icons';
import { productApi, storeApi } from '../../services/api';

const transferTypeMap = { 1: { text: '退库', color: 'orange' }, 2: { text: '调货', color: 'blue' }, 3: { text: '其他', color: 'default' }, 4: { text: '工厂分配', color: 'purple' } };
const returnStatusMap = { 0: { text: '待审核', color: 'blue' }, 1: { text: '已通过', color: 'green' }, 2: { text: '已拒绝', color: 'red' } };

const Returns = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [statusFilter, setStatusFilter] = useState('');
  const [transferTypeFilter, setTransferTypeFilter] = useState('');
  const [fromStoreFilter, setFromStoreFilter] = useState('');
  const [toStoreFilter, setToStoreFilter] = useState('');
  const [keyword, setKeyword] = useState('');
  const [stores, setStores] = useState([]);

  // 拒绝弹框
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectForm] = Form.useForm();
  const [currentReturnId, setCurrentReturnId] = useState(null);

  // 详情弹框
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);

  useEffect(() => {
    fetchStores();
    fetchData(1);
  }, []);

  const fetchStores = async () => {
    try {
      const res = await storeApi.list({ limit: 100 });
      if (res.success) setStores(res.data.list || []);
    } catch {}
  };

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: pagination.pageSize };
      if (statusFilter !== '') params.status = statusFilter;
      if (transferTypeFilter !== '') params.transfer_type = transferTypeFilter;
      if (fromStoreFilter) params.from_store_id = fromStoreFilter;
      if (toStoreFilter) params.to_store_id = toStoreFilter;
      if (keyword.trim()) params.keyword = keyword.trim();
      const res = await productApi.getReturns(params);
      if (res.success) {
        setData(res.data.list);
        setPagination({ ...pagination, current: page, total: res.data.pagination.total });
      }
    } catch (error) {
      message.error('获取调货记录失败');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await productApi.reviewReturn(id, { action: 'approve' });
      message.success('调货申请已通过');
      fetchData(pagination.current);
    } catch (error) {
      message.error(error.response?.data?.message || '审核失败');
    }
  };

  const showRejectModal = (id) => {
    setCurrentReturnId(id);
    rejectForm.resetFields();
    setRejectModalVisible(true);
  };

  const handleReject = async () => {
    try {
      const values = await rejectForm.validateFields();
      setRejectLoading(true);
      await productApi.reviewReturn(currentReturnId, { action: 'reject', reject_reason: values.reject_reason });
      message.success('调货申请已拒绝');
      setRejectModalVisible(false);
      fetchData(pagination.current);
    } catch (error) {
      message.error(error.response?.data?.message || '审核失败');
    } finally {
      setRejectLoading(false);
    }
  };

  const showDetail = async (orderId) => {
    setDetailModalVisible(true);
    setDetailLoading(true);
    try {
      const res = await productApi.getReturnDetail(orderId);
      if (res.success) {
        setDetailData(res.data);
      }
    } catch (error) {
      message.error('获取调货单详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const itemColumns = [
    { title: '货品编码', dataIndex: 'product_code', width: 120 },
    { title: '货品名称', dataIndex: 'product_name' },
    { title: '品类', dataIndex: 'category', width: 80 },
    { title: '材质', dataIndex: 'material', width: 80 },
    { title: '重量(g)', dataIndex: 'weight', width: 80 },
  ];

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '调货单号', dataIndex: 'order_no', width: 150 },
    {
      title: '调货类型', dataIndex: 'transfer_type', width: 90,
      render: (v) => {
        const t = transferTypeMap[v] || { text: '未知', color: 'default' };
        return <Tag color={t.color}>{t.text}</Tag>;
      }
    },
    { title: '来源门店', dataIndex: 'from_store_name', render: (v) => v === '工厂' ? '工厂' : (v || '-') },
    { title: '目标门店', dataIndex: 'to_store_name' },
    {
      title: '货品数量', dataIndex: 'product_count', width: 80,
      render: (v, record) => v || record.total_count || '-'
    },
    { title: '备注', dataIndex: 'remark', ellipsis: true },
    {
      title: '状态', dataIndex: 'status', width: 80,
      render: (v) => {
        const s = returnStatusMap[v] || { text: '未知', color: 'default' };
        return <Tag color={s.color}>{s.text}</Tag>;
      }
    },
    {
      title: '时间', dataIndex: 'created_at', width: 160,
      render: (v) => v ? new Date(v).toLocaleString('zh-CN') : '-'
    },
    {
      title: '审核信息', width: 160,
      render: (_, record) => {
        if (record.status === 0) return '-';
        return (
          <div style={{ fontSize: 12 }}>
            <div>{record.reviewer_name} · {record.reviewed_at ? new Date(record.reviewed_at).toLocaleString('zh-CN') : ''}</div>
            {record.status === 2 && record.reject_reason && <div style={{ color: '#999' }}>原因: {record.reject_reason}</div>}
          </div>
        );
      }
    },
    {
      title: '操作', width: 200, fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => showDetail(record.id)}>详情</Button>
          {record.status === 0 ? (
            <>
              <Button type="primary" size="small" icon={<CheckOutlined />} onClick={() => handleApprove(record.id)}>通过</Button>
              <Button danger size="small" icon={<CloseOutlined />} onClick={() => showRejectModal(record.id)}>拒绝</Button>
            </>
          ) : null}
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={() => fetchData(pagination.current)}>刷新</Button>
          <Select value={statusFilter || undefined} style={{ width: 120 }} placeholder="全部状态" allowClear
            onChange={(v) => { setStatusFilter(v ?? ''); }}>
            <Select.Option value={0}>待审核</Select.Option>
            <Select.Option value={1}>已通过</Select.Option>
            <Select.Option value={2}>已拒绝</Select.Option>
          </Select>
          <Select value={transferTypeFilter || undefined} style={{ width: 120 }} placeholder="全部类型" allowClear
            onChange={(v) => { setTransferTypeFilter(v ?? ''); }}>
            <Select.Option value={1}>退库</Select.Option>
            <Select.Option value={2}>调货</Select.Option>
            <Select.Option value={3}>其他</Select.Option>
            <Select.Option value={4}>工厂分配</Select.Option>
          </Select>
          <Select value={fromStoreFilter || undefined} style={{ width: 140 }} placeholder="来源门店" allowClear
            onChange={(v) => { setFromStoreFilter(v ?? ''); }}>
            <Select.Option value={0}>工厂</Select.Option>
            {stores.map(s => <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>)}
          </Select>
          <Select value={toStoreFilter || undefined} style={{ width: 140 }} placeholder="目标门店" allowClear
            onChange={(v) => { setToStoreFilter(v ?? ''); }}>
            {stores.map(s => <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>)}
          </Select>
          <Input.Search placeholder="搜索货品编码/名称" value={keyword}
            onChange={(e) => setKeyword(e.target.value)} onSearch={() => fetchData(1)} style={{ width: 180 }} />
        </Space>
      </div>

      <Table columns={columns} dataSource={data} loading={loading} rowKey="id" scroll={{ x: 1400 }}
        pagination={{ ...pagination, onChange: (page) => fetchData(page) }} />

      {/* 详情弹框 */}
      <Modal title={`调货单详情 ${detailData?.order?.order_no || ''}`} open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)} footer={null} width={700}>
        {detailLoading ? (
          <p>加载中...</p>
        ) : detailData ? (
          <>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="调货单号">{detailData.order.order_no}</Descriptions.Item>
              <Descriptions.Item label="调货类型">
                {(() => { const t = transferTypeMap[detailData.order.transfer_type] || {}; return <Tag color={t.color}>{t.text}</Tag>; })()}
              </Descriptions.Item>
              <Descriptions.Item label="来源门店">{detailData.order.from_store_name === '工厂' ? '工厂' : (detailData.order.from_store_name || '-')}</Descriptions.Item>
              <Descriptions.Item label="目标门店">{detailData.order.to_store_name}</Descriptions.Item>
              <Descriptions.Item label="货品数量">{detailData.order.total_count}</Descriptions.Item>
              <Descriptions.Item label="状态">
                {(() => { const s = returnStatusMap[detailData.order.status] || {}; return <Tag color={s.color}>{s.text}</Tag>; })()}
              </Descriptions.Item>
              <Descriptions.Item label="备注" span={2}>{detailData.order.remark || '-'}</Descriptions.Item>
              {detailData.order.status !== 0 && (
                <>
                  <Descriptions.Item label="审核人">{detailData.order.reviewer_name}</Descriptions.Item>
                  <Descriptions.Item label="审核时间">{detailData.order.reviewed_at ? new Date(detailData.order.reviewed_at).toLocaleString('zh-CN') : '-'}</Descriptions.Item>
                  {detailData.order.status === 2 && detailData.order.reject_reason && (
                    <Descriptions.Item label="拒绝原因" span={2}>{detailData.order.reject_reason}</Descriptions.Item>
                  )}
                </>
              )}
            </Descriptions>
            <Table columns={itemColumns} dataSource={detailData.items} rowKey="id" size="small" pagination={false} />
          </>
        ) : null}
      </Modal>

      {/* 拒绝原因弹框 */}
      <Modal title="拒绝调货申请" open={rejectModalVisible} onCancel={() => setRejectModalVisible(false)} onOk={handleReject} confirmLoading={rejectLoading}>
        <Form form={rejectForm} layout="vertical">
          <Form.Item name="reject_reason" label="拒绝原因" rules={[{ required: true, message: '请填写拒绝原因' }]}>
            <Input.TextArea rows={3} placeholder="请输入拒绝原因" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Returns;
